import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ProfilePageService } from '../../services/profile-page.service';
import { Employee, Farm, FarmLocations, GetUsersResponseType, Product, Review } from '../../../models/types';
import { LoginService } from '../../services/login.service';
import { UserInfo } from '../../../models/types';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { HttpResponse } from '@angular/common/http';
import { FullCalendarComponent } from "../../components/full-calendar/full-calendar.component";
import { MapComponent } from '../../components/map/map.component';
import { MapService } from '../../services/map.service';
import { FarmStatisticsPageComponent } from '../../farm-statistics-page/farm-statistics-page.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { 
  faMagnifyingGlass, 
  faCow,
  faStar,
  faLocationDot,
  faLocationCrosshairs, 
  faTractor,
  faEdit, 
  faTrash, 
  faUsers, 
  faUser,
  faPlus, 
  faClipboard,
  faEnvelope,
  faMessage,
  faPhone,
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { MarketPageService } from '../../services/market-page.service';
import { StarRatingModule } from 'angular-star-rating';
import { imageLocation } from '../../../environments/environment.development';

@Component({
  selector: 'app-product-list',
  standalone: true,
  imports: [CommonModule, FormsModule, FullCalendarComponent, MapComponent, FarmStatisticsPageComponent, FontAwesomeModule, StarRatingModule, RouterModule, RouterOutlet],
  templateUrl: './product-list.component.html',
  styleUrl: './product-list.component.scss'
})
export class ProductListComponent {
  @Input() allProducts: Product[] = [];
  @Input() onMarketPage: boolean = true;
  @Input() admin: boolean = false;
  @Input() farmID: number = 0;
  @Input() allFarms: Farm[] = [];
  @Input() onHomePage: boolean = false;

  farm: Farm = {
    userID: 0,
    farmID: 0,
    name: '',
    address: '',
    phone: '',
    email: '',
    employees: [],
    description: '',
    products: []
  };

  //Pagination part
  initialVisibleCount: number = 3; // Number of products to show initially
  incrementCount: number = 3; // Number of products to reveal each time
  revealedProductsCount: number = this.initialVisibleCount;
  showLoadMoreButton: boolean = false;

  allProductsShow: Product[] = [];
  filteredProducts: Product[] = [];
  productTypeFilter: string = '';
  costRangeFilter: string = '';
  searchFilter: string = '';
  farmCount: number = 0;
  prodCount: number = 0;
  farmLocations: FarmLocations[] = [];
  isOnMarketPage: boolean = true;
  loggedInUserInfo: UserInfo | undefined;
  products: Product[] = [];
  errorMessage: string = '';
  previousVisibleCount: number = 0;

  vegetableImageUrl = '../../../assets/images/generic/vegetable.png';
  meatImageUrl = '../../../assets/images/generic/meat.png';
  dairyImageUrl = '../../../assets/images/generic/dairy.png';
  otherImageUrl = '../../../assets/images/generic/other.png';

  farmDefaultImageUrl = '../../../assets/images/generic/farm.png';

  editingProductIndex: number = -1; 
  newProduct: Product = {
    productID: 0,
    farmID: 0,
    name: '',
    description: '',
    cost: 0,
    quantity: 0,
    type: 'ostalo',
    image: ''
  };

  faMagnifyingGlass = faMagnifyingGlass;
  faCow = faCow;
  faStar = faStar;
  faLocationDot = faLocationDot;
  faLocationCrosshairs = faLocationCrosshairs;
  faTractor = faTractor;
  faEdit = faEdit;
  faTrash = faTrash;
  faUsers = faUsers;
  faUser = faUser;
  faPlus = faPlus;
  faClipboard = faClipboard;
  faEnvelope = faEnvelope;
  faPhone = faPhone;
  faMessage = faMessage;

  constructor(
    private marketPageService: MarketPageService,
    private loginService: LoginService,
    private profilePageService: ProfilePageService,
    private router: Router) { }

  ngOnInit(): void {
    console.log('onMarketPage:', this.onMarketPage);
    console.log('admin:', this.admin);
    if (this.onMarketPage) {
      // Load all products
      console.log("from product-list.component.ts: ", this.allProducts);
    } else if (!this.admin) {
      // load products for guest on farm page
      console.log("from product-list.component.ts: ", this.allProducts);
    } else {
      this.initialVisibleCount = 10;
      console.log('FarmProfilePage initialized.');
      this.loggedInUserInfo = this.loginService.GetLoggedInUserInfo();
      if (this.loggedInUserInfo) {
        // Load farm profile based on loggedInUserInfo.userID
        this.fetchFarmData();
      } else {
        // Handle scenario where user is not logged in or userInfo is not available
      }
      console.log('User info:', this.loggedInUserInfo);
    }
    this.initializeProducts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allProducts'] && !changes['allProducts'].firstChange) {
      console.log('Products input changed:', this.allProducts);
      // Perform actions based on the updated products input
      this.products = this.allProducts; // Example action
      this.setFarmNamesToProducts();
      this.initializeProducts();
    }
  }

  initializeProducts(): void {
    this.revealedProductsCount = Math.min(this.allProducts.length, this.initialVisibleCount);
    this.allProductsShow = this.allProducts.slice(0, this.revealedProductsCount);
    this.showLoadMoreButton = this.allProducts.length > this.revealedProductsCount;
    console.log('AllProductsShow:', this.allProductsShow);
  }

  revealMoreProducts(): void {
    this.previousVisibleCount = this.revealedProductsCount;
    this.revealedProductsCount = Math.min(this.allProducts.length, this.revealedProductsCount + this.incrementCount);
    this.allProductsShow = this.allProducts.slice(0, this.revealedProductsCount);
    this.showLoadMoreButton = this.revealedProductsCount < this.allProducts.length;
  
    console.log('showLoadMoreButton:', this.showLoadMoreButton);
    console.log('number of revealed products:', this.revealedProductsCount);
    console.log('number of all products:', this.allProducts.length);
  }
  
  fetchFarmData(): void {
    const loggedInUserInfo = this.loginService.GetLoggedInUserInfo();
    if (loggedInUserInfo) {
      this.profilePageService.getFarmInfo(loggedInUserInfo.userID).subscribe(
        (response) => {
          this.farm = response.body!;
          this.products = this.assignProductImages(this.farm.products);
          console.log("Products:", this.products);
        },
        (error) => {
          this.errorMessage = 'Error loading farm data';
          console.error('Error loading farm data:', error);
        }
      );
    }
  }

  private assignProductImages(products: Product[]): Product[] {
    return products.map(product => {
      product.averageRating = this.calculateAverageRating(product.reviews);
      
      let defaultImageUrl;
      switch (product.type) {
        case 'zelenjava':
          defaultImageUrl = this.vegetableImageUrl;
          break;
        case 'mlečni izdelek':
          defaultImageUrl = this.dairyImageUrl;
          break;
        case 'meso':
          defaultImageUrl = this.meatImageUrl;
          break;
        default:
          defaultImageUrl = this.otherImageUrl;
          break;
      }
  
      if (!product.image) {
        product.image = defaultImageUrl;
      } else {
        product.image = `${imageLocation}/static/content/${product.productID}/${product.image}`;
      }
      return product;
    });
  }

  calculateAverageRating(reviews?: Review[]): number {
    if (reviews && reviews.length > 0) {
      const total = reviews.reduce((sum, review) => sum + review.rating, 0);
      return total / reviews.length;
    }
    return 0;
  }
  

  openContactModal(product: Product) {
    if (this.loginService.IsUserLoggedIn()) {
      this.marketPageService.openContactModal(product);
    } else {
      Swal.fire({
        title: 'Obvezna prijava',
        text: 'Za kontakt s prodajalcem se morate prijaviti v sistem.',
        icon: 'info',
        confirmButtonText: 'OK'
      });
    }
  }

  openReviewModal(event: Event, product: Product) {
    event.preventDefault();      
    this.marketPageService.openReviewModal(product);
  }

  getTruncatedProductDescription(description: string): string {
    return description.length > 240 ? description.slice(0, 240) : description;
  }

  openUpdateProductModal(product: Product) {
    this.profilePageService.openProduct(product);
  }

  openAddProductModal(): void {
    this.profilePageService.openProductAdd(this.farm);
  }
  setFarmNamesToProducts(): void {
    this.products.forEach((product) => {
      const farm = this.allFarms.find((farm) => farm.farmID === product.farmID);
      console.log('Farm:', farm);
      if (farm) {
        product.farmName = farm.name;
      }
    });
    console.log('Products with farm names:', this.products);
    this.allProductsShow = this.products;
  }
  

  removeProduct(index: number, productID: number): void {
    Swal.fire({
      title: 'Ste prepričani?',
      text: "Ali želite odstraniti ta izdelek?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Da, odstrani!',
      cancelButtonText: 'Prekliči'
    }).then((result) => {
      if (result.isConfirmed) {
        console.log('Removing product:', this.products[index].productID + ' from farm: ' + this.farm.farmID + ' at index: ' + index + '...');
        this.profilePageService.deleteProduct(this.farm.farmID, productID).subscribe(
          (response: HttpResponse<any>) => {
            console.log('Product removed successfully:', response);
            // Remove product from local products array
            this.farm.products.splice(index, 1);
            this.products.splice(index, 1);
            Swal.fire(
              'Odstranjeno!',
              'Izdelek je bil uspešno odstranjen.',
              'success'
            );
          },
          (error: any) => {
            console.error('Error removing product:', error);
            // Handle error removing product
            Swal.fire(
              'Napaka!',
              'Prišlo je do napake pri odstranjevanju izdelka.',
              'error'
            );
          }
        );
        //window.location.reload();
      }
    });
  }

}
